import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  FormHelperText, Grid,
  InputLabel, LinearProgress, Link, MenuItem, Select, Stack, TextField,
  Typography,
} from '@mui/material';
import {
  RocketLaunch, Visibility, OpenInNew, Edit, AudioFile, Image, CloudUpload,
  YouTube,
} from '@mui/icons-material';
import Panel from '../crt/Panel';
import SectionTitle from '../crt/SectionTitle';
import {
  DraftRequest, episodesAPI, uploadsAPI, YouTubeJob, WpSeason, PatreonMatch,
  MegaphoneVideoJob,
} from '../../services/api';
import PendingReleasesPanel from './PendingReleasesPanel';
import MidrollPlacer from './MidrollPlacer';
import PatreonPublishPanel from './PatreonPublishPanel';

type PreviewResp = {
  site: string;
  post_status: string;
  post_title: string;
  post_name: string;
  post_excerpt: string;
  post_date: string;
  yoast_title: string;
  yoast_metadesc: string;
  yoast_focuskw: string;
  og_title: string;
  og_description: string;
  twitter_title: string;
  twitter_description: string;
  season_post_id: number | null;
  tag_canon: Array<[string, string]>;
  post_content: string;
};

type CreateResp = {
  post_id: number;
  site: string;
  post_status: string;
  edit_url: string;
  preview_url: string;
  canonical_url: string;
  tags_applied: number;
  yoast_title: string;
  yoast_metadesc: string;
  yoast_focuskw: string;
};

// Mirror of backend wordpress_episode_draft.SITE_PROFILE — used to
// compute the templated social-card defaults client-side so the
// placeholder/helper text shows the EXACT string that will be used,
// without the user having to click Preview first.
const SITE_PROFILE = {
  spoutlore: { show_name: 'Spout Lore' },
  mallbrats: { show_name: 'Mall Brats' },
} as const;

const stripHtml = (s: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = s || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

// Mirror backend wordpress_episode_draft.compose_draft_plan logic.
// Yoast title format (mined from every existing episode):
//   season-numbered ep:  "%%sitename%% %%sep%% Season N %%sep%% %%title%% %%page%%"
//   no season number:    "{title} | {show_name}" (static)
// We show what Yoast WILL RENDER at page-load (sitename / sep / title
// substituted) so the user sees real strings, but flag it as templated.
const computeYoastTitle = (
  title: string,
  site: 'spoutlore' | 'mallbrats',
  season?: string | number | null,
): string => {
  const t = title.trim();
  if (!t) return '';
  const showName = SITE_PROFILE[site].show_name;
  const seasonNum = season != null && season !== '' ? Number(season) : NaN;
  if (Number.isFinite(seasonNum)) {
    // Show what Yoast will render — but the actual postmeta is the
    // template string with %%tokens%% (this is what existing posts
    // store; Yoast substitutes at render time).
    return `${showName} | Season ${seasonNum} | ${t}`;
  }
  const composed = `${t} | ${showName}`;
  return composed.length > 70 ? t.slice(0, 70) : composed;
};

// Strip any "Episode N -" / "Ep N -" / "SxxEyy -" prefix from a user
// input so we can re-prefix it differently per surface.
const _TITLE_PREFIX_RE_JS =
  /^\s*(?:S\d{1,3}E\d{1,3}|EPISODE\s+\d+|EP\.?\s+\d+)\s*[-:—–]\s*/i;

const stripEpisodePrefix = (s: string): string =>
  (s || '').replace(_TITLE_PREFIX_RE_JS, '').trim();

// Compose the WP post_title from a bare hook + episode number.
// Existing convention: "Episode {N} - {hook}" for numbered episodes;
// hook alone for bonus/trailer episodes (no episode number).
// Idempotent: if the user already typed "Episode 4 - foo", we
// re-derive cleanly.
const computeWpTitle = (
  hookOrFullTitle: string,
  episode: string | number | null | undefined,
): string => {
  const hook = stripEpisodePrefix(hookOrFullTitle);
  if (!hook) return '';
  const e = episode != null && episode !== '' ? Number(episode) : NaN;
  if (!Number.isFinite(e)) return hook;
  return `Episode ${e} - ${hook}`;
};

// Compose the YouTube title: "{show} | TTRPG Podcast | S{NN}E{NN} - {hook}".
// Mined from every existing SL VOD-playlist video:
//   "Spout Lore | TTRPG Podcast | S14E02 - She's Just Not That Into You"
// Bonus episodes (no season/episode) fall back to "{show} | TTRPG Podcast | {hook}".
// YouTube hard-caps titles at 100 chars; we trim the hook if needed.
const computeYoutubeTitle = (
  userTitle: string,
  site: 'spoutlore' | 'mallbrats',
  season: string | number | null | undefined,
  episode: string | number | null | undefined,
): string => {
  const hook = stripEpisodePrefix(userTitle);
  if (!hook) return '';
  const showName = SITE_PROFILE[site].show_name;
  const s = season != null && season !== '' ? Number(season) : NaN;
  const e = episode != null && episode !== '' ? Number(episode) : NaN;
  const pad = (n: number) => String(n).padStart(2, '0');
  let title: string;
  if (Number.isFinite(s) && Number.isFinite(e)) {
    title = `${showName} | TTRPG Podcast | S${pad(s)}E${pad(e)} - ${hook}`;
  } else {
    title = `${showName} | TTRPG Podcast | ${hook}`;
  }
  // YouTube cap is 100 chars — trim the hook end if needed
  if (title.length > 100) {
    const overflow = title.length - 100;
    const trimmedHook = hook.slice(0, Math.max(0, hook.length - overflow - 1)) + '…';
    title = Number.isFinite(s) && Number.isFinite(e)
      ? `${showName} | TTRPG Podcast | S${pad(s)}E${pad(e)} - ${trimmedHook}`
      : `${showName} | TTRPG Podcast | ${trimmedHook}`;
  }
  return title;
};

// Compose the Megaphone title: SxxEyy prefix + hook. Mirrors backend
// _clean_megaphone_title. Without this conversion, podcast apps would
// render "S14E04: Episode 4 - Foo" (the 'Episode 4' becomes redundant
// noise because apps prepend SxxEyy from the numeric fields).
const computeMegaphoneTitle = (
  userTitle: string,
  season: string | number | null | undefined,
  episode: string | number | null | undefined,
): string => {
  const hook = stripEpisodePrefix(userTitle);
  if (!hook) return '';
  const s = season != null && season !== '' ? Number(season) : NaN;
  const e = episode != null && episode !== '' ? Number(episode) : NaN;
  if (!Number.isFinite(s) || !Number.isFinite(e)) return hook;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `S${pad(s)}E${pad(e)} - ${hook}`;
};

// <input type="datetime-local"> returns a naive ISO string in browser
// local time. Convert it to a UTC ISO string for the API. Returns null
// for empty/invalid input.
const localDateTimeToUtcIso = (localStr: string): string | null => {
  if (!localStr) return null;
  const d = new Date(localStr);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Mirror backend build_social_description: strip HTML, cap at 200 chars
// at a sentence/word boundary, NO podcast-tagline suffix.
const computeSocialDescription = (description: string, maxChars = 200): string => {
  const raw = stripHtml(description);
  if (!raw) return '';
  if (raw.length <= maxChars) return raw;
  const floor = Math.max(0, Math.floor(maxChars * 0.7));
  const slice = raw.slice(0, maxChars);
  for (const ch of ['.', '!', '?']) {
    const idx = slice.lastIndexOf(ch, maxChars);
    if (idx >= floor) return raw.slice(0, idx + 1).trim();
  }
  const cut = slice.lastIndexOf(' ');
  return (cut > 0 ? slice.slice(0, cut) : slice).trim() + '…';
};

// Compact wizard progress indicator. The flow is really 4 stacked panels in a
// long scroll; this bar makes it read as a wizard and shows where you are
// (active = first not-done step, cyan) and what's finished (✓, green).
const WizardSteps: React.FC<{
  createdDone: boolean;
  megaphoneDone: boolean;
  youtubeDone: boolean;
  publishDone: boolean;
}> = ({ createdDone, megaphoneDone, youtubeDone, publishDone }) => {
  const steps = [
    { n: 1, label: 'WP DRAFT', done: createdDone },
    { n: 2, label: 'MEGAPHONE', done: megaphoneDone },
    { n: 3, label: 'YOUTUBE', done: youtubeDone },
    { n: 4, label: 'PUBLISH', done: publishDone },
  ];
  const activeIdx = steps.findIndex((s) => !s.done); // -1 once all done
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflowX: 'auto',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
        {steps.map((s, i) => {
          const active = i === activeIdx;
          const color = s.done ? 'success.main' : active ? '#00ffff' : 'text.secondary';
          return (
            <React.Fragment key={s.n}>
              <Typography
                variant="caption"
                sx={{ color, fontWeight: active || s.done ? 700 : 400, fontFamily: 'monospace' }}
              >
                {s.done ? '✓' : s.n}&nbsp;{s.label}
              </Typography>
              {i < steps.length - 1 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>→</Typography>
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};

const PublishEpisodeTab: React.FC = () => {
  // Form state
  const [site, setSite] = useState<'spoutlore' | 'mallbrats'>('spoutlore');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [seasonNumber, setSeasonNumber] = useState('');
  // WP season pages for the dropdown + the explicitly chosen season post id.
  // seasonPostId='' means "no season"; a numbered season also sets seasonNumber
  // (for Megaphone seasonNumber + JSON-LD), a named page (Patreon Teasers etc.)
  // leaves seasonNumber blank but still attaches via the post id.
  const [seasons, setSeasons] = useState<WpSeason[]>([]);
  const [seasonPostId, setSeasonPostId] = useState('');
  // "Patreon teaser" release-type preset: when on, the wizard pre-fills the
  // teaser pattern (Patreon Teasers season, bonus, video) + shows a guided
  // checklist. See applyPatreonTeaserPreset().
  const [patreonMode, setPatreonMode] = useState(false);
  // Patreon-ONLY mode: post straight to Patreon (own media + tiers) with NO
  // Megaphone episode and NO website post. Bypasses the whole episode pipeline.
  const [patreonOnly, setPatreonOnly] = useState(false);
  // "Pull from Patreon": type-ahead search of synced posts → autofill desc + CW.
  const [patreonOptions, setPatreonOptions] = useState<PatreonMatch[]>([]);
  const [patreonInput, setPatreonInput] = useState('');
  const [patreonLoading, setPatreonLoading] = useState(false);
  const [patreonPullMsg, setPatreonPullMsg] = useState<string | null>(null);
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [contentWarning, setContentWarning] = useState('');
  const [extraBody, setExtraBody] = useState('');
  // Mirror of the publish_sync cron's `enabled` flag in etl_schedule.
  // Flip BOTH when (re-)enabling: this constant + the row in
  // public.etl_schedule WHERE job_name='publish_sync'.
  const PUBLISH_SYNC_CRON_ENABLED = false;
  const [autoPublish, setAutoPublish] = useState(PUBLISH_SYNC_CRON_ENABLED);
  // Optional social-card overrides. Blank = Yoast cascades from SEO
  // title/metadesc (i.e. FB share copy == SERP copy).
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [twitterTitle, setTwitterTitle] = useState('');
  const [twitterDescription, setTwitterDescription] = useState('');

  // API state
  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [created, setCreated] = useState<CreateResp | null>(null);
  const [busy, setBusy] = useState<'idle' | 'previewing' | 'creating' | 'resuming'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Resume by post_id — when the user has a WP draft already created
  // (e.g. tab died between Step 1 and Step 2) and wants to pick up.
  const [resumePostId, setResumePostId] = useState('');
  // Live state of the Megaphone episode (if any) linked to the
  // currently-loaded WP post. Hydrated by /draft-detail on Resume and by
  // /megaphone-publish on a fresh create. When set, Step 2 must NOT
  // offer the "create episode" button (would make a duplicate); instead
  // it offers "attach audio to existing episode".
  const [existingMegaphone, setExistingMegaphone] = useState<{
    id: string;
    image_url: string | null;
    thumbnail_url: string | null;
    audio_url: string | null;
    audio_uploaded: boolean;
    draft: boolean | null;
    status: string | null;
    pubdate: string | null;
    clean_title: string | null;
  } | null>(null);
  // Same pattern for YouTube: when a video is already linked to this WP
  // post, Step 3 must NOT show the upload button (which would burn
  // ~1600 quota units and create a duplicate video on the channel).
  const [existingYouTube, setExistingYouTube] = useState<{
    video_id: string;
    title: string | null;
    thumbnail_url: string | null;
    privacy: string | null;
    upload_status: string | null;
    watch_url: string | null;
    published_at: string | null;
    view_count: number | null;
  } | null>(null);
  const onResume = async (postId: number, resumeSite: string) => {
    setError(null);
    setBusy('resuming');
    try {
      const d = await episodesAPI.draftDetail(postId, resumeSite);
      // Hydrate the Step 1 form fields from the existing draft so the
      // helper-text previews work, then synthesize the `created` state
      // to unlock Step 2/3 panels (which key on `created !== null`).
      setSite(d.site as 'spoutlore' | 'mallbrats');
      // Strip the SxxEyy / Episode-N prefix so the title input shows the
      // hook only — matches the post-#33 UX where users type the hook
      // and the wizard reattaches the prefix.
      setTitle(stripEpisodePrefix(d.post_title || ''));
      setDescription(d.description || '');
      if (d.season_number != null) setSeasonNumber(String(d.season_number));
      // Restore the chosen season PAGE directly — this is what makes named
      // pages (Patreon Teasers, Trailers, …) resume correctly; the numeric
      // season_number can't represent them.
      if (d.season_post_id != null) setSeasonPostId(String(d.season_post_id));
      if (d.episode_number != null) setEpisodeNumber(String(d.episode_number));
      // Restore the content warning the user typed at draft time (parsed back
      // out of the post body's [Content Warning: …] block by the backend).
      setContentWarning(d.content_warning || '');
      // Pre-fill the release-date input. Prefer the release date the user set
      // at draft time (_autopod_release_at); fall back to Megaphone's pubdate
      // on the linked episode. Both are ISO-UTC; the input wants browser-local
      // "YYYY-MM-DDTHH:MM".
      const isoDate = d.release_date || d.megaphone_pubdate;
      if (isoDate) {
        try {
          const dt = new Date(isoDate);
          const pad = (n: number) => String(n).padStart(2, '0');
          setReleaseDate(
            `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}` +
            `T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
          );
        } catch { /* fall through */ }
      }
      // Build a synthetic CreateResp — only the fields Step 2/3 read.
      setCreated({
        post_id: d.post_id,
        site: d.site,
        post_status: d.post_status,
        edit_url: `${(d as any).canonical_url?.replace(/\/episodes\/.*/, '') || ''}/wp-admin/post.php?post=${d.post_id}&action=edit`,
        preview_url: d.canonical_url || '',
        canonical_url: d.canonical_url || '',
        tags_applied: 0,
        yoast_title: '', yoast_metadesc: '', yoast_focuskw: '',
      });
      // Persist Megaphone state if the post already has a linked episode.
      // Step 2 reads this to decide between "create episode" vs the
      // safer "attach audio to existing episode" flow.
      if (d.megaphone_id) {
        setExistingMegaphone({
          id: d.megaphone_id,
          image_url: d.megaphone_image_url || null,
          thumbnail_url: d.megaphone_thumbnail_url || null,
          audio_url: d.megaphone_audio_url || null,
          audio_uploaded: !!d.megaphone_audio_uploaded,
          draft: d.megaphone_draft ?? null,
          status: d.megaphone_status || null,
          pubdate: d.megaphone_pubdate || null,
          clean_title: d.megaphone_clean_title || null,
        });
      } else {
        setExistingMegaphone(null);
      }
      // Same for YT — when the linked video_id is present, hydrate
      // existingYouTube so Step 3 can show the saved state and hide
      // the destructive re-upload button.
      if (d.youtube_id) {
        setExistingYouTube({
          video_id: d.youtube_id,
          title: d.youtube_title || null,
          thumbnail_url: d.youtube_thumbnail_url || null,
          privacy: d.youtube_privacy || null,
          upload_status: d.youtube_upload_status || null,
          watch_url: d.youtube_watch_url
            || `https://www.youtube.com/watch?v=${d.youtube_id}`,
          published_at: d.youtube_published_at || null,
          view_count: typeof d.youtube_view_count === 'number'
            ? d.youtube_view_count : null,
        });
      } else {
        setExistingYouTube(null);
      }
      // ALSO check for any persisted YT upload job tied to this post.
      // A failed job with retryable=true means the video is already
      // staged in S3 and we can re-run the YT-API step without making
      // the user re-upload (saves multi-GB transfer when only OAuth /
      // YT-API failed). Surface the latest failed job back into ytJob
      // so the polling loop + retry UI in Step 3 picks it up.
      try {
        const { jobs } = await episodesAPI.youtubeJobsForPost(d.post_id);
        const latestRetryable = jobs.find(
          (j) => j.status === 'error' && j.retryable,
        );
        if (latestRetryable && !d.youtube_id) {
          setYtJob(latestRetryable);
          setYtError(latestRetryable.error);
          setYtPhase('idle');
        }
      } catch (_e) {
        // Best-effort — failure to surface old jobs doesn't block resume.
      }
      setResumePostId('');
      // Scroll to the Step 2 panel after state settles (ref, not the old
      // data-step querySelector which never matched — MUI Paper drops it).
      setTimeout(() => {
        step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Resume failed');
    } finally {
      setBusy('idle');
    }
  };

  // Step 2 (Megaphone) state
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [artworkFile, setArtworkFile] = useState<File | null>(null);
  // Dedicated Megaphone video thumbnail/poster (16:9), distinct from the cover.
  const [mpVideoThumbFile, setMpVideoThumbFile] = useState<File | null>(null);
  const [mpVideoThumbPct, setMpVideoThumbPct] = useState(0);
  const [audioPct, setAudioPct] = useState(0);
  const [artworkPct, setArtworkPct] = useState(0);
  const [mpMediaKind, setMpMediaKind] = useState<'audio' | 'video'>('audio');
  // Megaphone episodeType + explicit rating — matches the existing-
  // episode pattern (regular eps = full + clean; Patreon bonus eps
  // override to 'bonus' + 'unspecified').
  const [mpEpisodeType, setMpEpisodeType] = useState<'full' | 'trailer' | 'bonus'>('full');
  const [mpExplicit, setMpExplicit] = useState<'clean' | 'explicit' | 'unspecified'>('clean');
  const [mpStatus, setMpStatus] = useState<
    'idle' | 'uploading_audio' | 'uploading_artwork' | 'creating_episode' | 'done'
  >('idle');
  const [mpError, setMpError] = useState<string | null>(null);
  const [mpResult, setMpResult] = useState<any | null>(null);
  // Non-fatal step failures during a Megaphone publish (client-side steps +
  // the backend's returned `warnings`), surfaced in the UI so a partial
  // publish is never silent.
  const [mpWarnings, setMpWarnings] = useState<string[]>([]);
  const [midrollOpen, setMidrollOpen] = useState(false);
  // Programmatic Megaphone VIDEO upload job (async — staged S3 → upload → create).
  const [mpVideoJob, setMpVideoJob] = useState<MegaphoneVideoJob | null>(null);
  const mpVideoPollRef = useRef<number | null>(null);

  // Step 3 (YouTube) state
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [videoPct, setVideoPct] = useState(0);
  const [thumbnailPct, setThumbnailPct] = useState(0);
  const [ytPrivacy, setYtPrivacy] = useState<'private' | 'unlisted' | 'public'>('private');
  const [ytAddToPlaylist, setYtAddToPlaylist] = useState(true);
  const [ytPlaylists, setYtPlaylists] = useState<
    Array<{ id: string; title: string; item_count: number | null }>>([]);
  const [ytPlaylistId, setYtPlaylistId] = useState<string>('');
  const [ytPlaylistsLoading, setYtPlaylistsLoading] = useState(false);
  // Optional overrides. Blank = use the main `title` / `description`.
  // YT-specific titles often differ (different keyword surface, 100-char
  // hard cap, search-driven phrasing) — and YT descriptions get 5000
  // chars vs the ~160-char Megaphone summary.
  const [ytTitle, setYtTitle] = useState('');
  const [ytDescription, setYtDescription] = useState('');
  const [ytPhase, setYtPhase] = useState<
    'idle' | 'uploading_video' | 'uploading_thumbnail' | 'queued' | 'done'
  >('idle');
  const [ytJob, setYtJob] = useState<YouTubeJob | null>(null);
  const [ytError, setYtError] = useState<string | null>(null);
  const ytPollRef = useRef<number | null>(null);
  // Scroll anchor for the Megaphone step. The Panel renders its label via a
  // CSS ::before pseudo-element and MUI's Paper drops unknown data-* props, so
  // the old `document.querySelector('[data-step=...]')` matched nothing and the
  // scroll silently no-op'd. A real DOM ref is reliable.
  const step2Ref = useRef<HTMLDivElement | null>(null);

  // Step 4 (Publish Everywhere) state — single-button flip across WP +
  // Megaphone + YouTube. Calls /publish-now, which already handles all
  // four targets server-side (WP draft→publish, Megaphone draft→live,
  // YT private→public, episode_url postmeta + JSON-LD refresh).
  const [publishStatus, setPublishStatus] = useState<
    'idle' | 'publishing' | 'done' | 'error'
  >('idle');
  const [publishResult, setPublishResult] = useState<any | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishForce, setPublishForce] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

  const onPublishEverywhere = async () => {
    if (!created) return;
    setPublishError(null);
    setPublishResult(null);
    setPublishStatus('publishing');
    try {
      const result = await episodesAPI.publishNow({
        site,
        wp_post_id: created.post_id,
        force: publishForce,
        // Forward season/episode so the backend can tell jeeves to mark this
        // production episode released + complete its todo tree.
        season_number: seasonNumber !== '' ? Number(seasonNumber) : null,
        episode_number: episodeNumber !== '' ? Number(episodeNumber) : null,
        title: title || null,
      });
      setPublishResult(result);
      setPublishStatus('done');
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      // 409 = audio_url-ready gate failed (Megaphone hasn't transcoded
      // yet). Surface a specific message + offer the force flag.
      const is409 = e?.response?.status === 409;
      setPublishError(
        is409
          ? `${detail || 'Megaphone audio not ready yet'} — check the "Force" box below and retry to publish anyway.`
          : (detail || e?.message || 'Publish failed')
      );
      setPublishStatus('error');
    } finally {
      setPublishConfirmOpen(false);
    }
  };

  // Load the site's WP season pages for the dropdown whenever the site changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { seasons: list } = await episodesAPI.listSeasons(site);
        if (!cancelled) setSeasons(list);
      } catch {
        if (!cancelled) setSeasons([]);
      }
    })();
    return () => { cancelled = true; };
  }, [site]);

  // Autofill season/episode for a FRESH publish from the show's Megaphone feed
  // (max season, max-episode-in-that-season + 1). Skips resume (created /
  // resumePostId) and the Patreon-teaser preset, and never clobbers a value the
  // user has already entered. Shows that don't number their feed (has_numbering
  // = false) leave the fields blank.
  useEffect(() => {
    if (created || resumePostId || patreonMode) return;
    let cancelled = false;
    (async () => {
      try {
        const n = await episodesAPI.nextNumbering(site);
        if (cancelled || !n.has_numbering) return;
        const sn = n.season_number, en = n.episode_number;
        if (sn != null) setSeasonNumber((prev) => (prev === '' ? String(sn) : prev));
        if (en != null) setEpisodeNumber((prev) => (prev === '' ? String(en) : prev));
      } catch {
        /* best-effort; leave the fields blank on any error */
      }
    })();
    return () => { cancelled = true; };
  }, [site, created, resumePostId, patreonMode]);

  // Load the channel's YouTube playlists for the VOD dropdown whenever the
  // site changes; default the selection to the per-site VOD playlist.
  useEffect(() => {
    let cancelled = false;
    setYtPlaylistsLoading(true);
    (async () => {
      try {
        const r = await episodesAPI.youtubePlaylists(site);
        if (cancelled) return;
        setYtPlaylists(r.playlists || []);
        setYtPlaylistId((prev) => prev || r.default_playlist_id || '');
      } catch {
        if (!cancelled) setYtPlaylists([]);
      } finally {
        if (!cancelled) setYtPlaylistsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [site]);

  // After seasons load (e.g. on resume where only season_number is known),
  // preselect the matching numbered season page if nothing is chosen yet.
  useEffect(() => {
    if (seasonPostId || !seasonNumber || seasons.length === 0) return;
    const match = seasons.find((s) => s.season_number === Number(seasonNumber));
    if (match) setSeasonPostId(String(match.post_id));
  }, [seasons, seasonNumber, seasonPostId]);

  // Patreon-teaser preset: once the (spoutlore) seasons load, auto-select the
  // "Patreon Teasers" page by title (id resolved live, not hardcoded).
  useEffect(() => {
    if (!patreonMode || seasons.length === 0) return;
    const pt = seasons.find((s) => /patreon\s*teaser/i.test(s.title));
    if (pt && seasonPostId !== String(pt.post_id)) {
      setSeasonPostId(String(pt.post_id));
      setSeasonNumber('');
    }
  }, [patreonMode, seasons, seasonPostId]);

  // Patreon-teaser preset: once playlists load, auto-select the "Patreon
  // Teasers" YouTube playlist by title (id resolved live, not hardcoded).
  useEffect(() => {
    if (!patreonMode || ytPlaylists.length === 0) return;
    const pt = ytPlaylists.find((p) => /patreon\s*teaser/i.test(p.title || ''));
    if (pt && ytPlaylistId !== pt.id) setYtPlaylistId(pt.id);
  }, [patreonMode, ytPlaylists, ytPlaylistId]);

  // Apply / clear the Patreon-teaser release-type preset.
  const applyPatreonTeaserPreset = () => {
    setPatreonMode(true);
    setSite('spoutlore');          // the main feed that carries Patreon teasers
    setSeasonNumber('');
    setEpisodeNumber('');          // teasers carry no S/E numbers
    setMpMediaKind('video');       // video teaser in the feed
    setMpEpisodeType('bonus');     // never 'trailer'
    setMpExplicit('unspecified');  // matches existing teasers (no rating)
    setYtAddToPlaylist(true);      // teasers go on the "Patreon Teasers" YT playlist
    // season + YT playlist auto-select via the effects above/below once
    // spoutlore seasons + playlists load.
  };
  const clearPatreonTeaserPreset = () => {
    setPatreonMode(false);
    setSeasonPostId('');
    setMpMediaKind('audio');
    setMpEpisodeType('full');
    setMpExplicit('clean');
    setYtAddToPlaylist(true);
  };

  // Type-ahead search of synced Patreon posts (debounced). Empty input → recent.
  useEffect(() => {
    if (!patreonMode) return;
    let cancelled = false;
    setPatreonLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const { matches } = await episodesAPI.pullFromPatreon(patreonInput);
        if (!cancelled) setPatreonOptions(matches);
      } catch {
        if (!cancelled) setPatreonOptions([]);
      } finally {
        if (!cancelled) setPatreonLoading(false);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [patreonInput, patreonMode]);

  // On selecting a post: fill description (before [Content Warning:]) + the CW
  // terms; everything after the block was discarded server-side.
  const onSelectPatreonPost = (m: PatreonMatch | null) => {
    if (!m) return;
    if (m.suggested_title) setTitle(m.suggested_title);
    setDescription(m.description || '');
    setContentWarning(m.content_warning || '');
    setPatreonPullMsg(
      `Pulled from “${m.title}” → title, description${m.had_content_warning ? ' + content warning' : ' (no [Content Warning:] block — CW left blank)'}. Edit below as needed.`,
    );
  };

  // Poll the Megaphone VIDEO job until done/error.
  useEffect(() => {
    if (!mpVideoJob || mpVideoJob.status === 'done' || mpVideoJob.status === 'error') {
      if (mpVideoPollRef.current) {
        window.clearInterval(mpVideoPollRef.current);
        mpVideoPollRef.current = null;
      }
      if (mpVideoJob && mpVideoJob.status === 'done') {
        setMpStatus('done');
        setMpResult({
          megaphone_episode_id: mpVideoJob.episode_id,
          cms_edit_url: mpVideoJob.cms_edit_url,
          draft: true,
        });
        if (mpVideoJob.episode_id) {
          setExistingMegaphone({
            id: mpVideoJob.episode_id,
            image_url: null,
            thumbnail_url: null,
            audio_url: null,
            audio_uploaded: true,           // the video media IS attached
            draft: true,
            status: null,
            pubdate: localDateTimeToUtcIso(releaseDate),
            clean_title: stripEpisodePrefix(title) || null,
          });
        }
      }
      return;
    }
    if (!mpVideoPollRef.current) {
      mpVideoPollRef.current = window.setInterval(async () => {
        try {
          const u = await episodesAPI.megaphoneVideoJob(mpVideoJob.job_id);
          setMpVideoJob(u);
        } catch (e: any) {
          setMpError(e?.message || 'Megaphone video job poll failed');
        }
      }, 4000);
    }
    return () => {
      if (mpVideoPollRef.current) {
        window.clearInterval(mpVideoPollRef.current);
        mpVideoPollRef.current = null;
      }
    };
  }, [mpVideoJob]);

  // Poll the YT job until done/error
  useEffect(() => {
    if (!ytJob || ytJob.status === 'done' || ytJob.status === 'error') {
      if (ytPollRef.current) {
        window.clearInterval(ytPollRef.current);
        ytPollRef.current = null;
      }
      if (ytJob && ytJob.status === 'done') {
        setYtPhase('done');
        // Reflect the freshly-uploaded video in existingYouTube so the
        // Step 3 panel re-renders with the "already uploaded" banner +
        // disabled upload button — preventing the user from re-clicking
        // Upload and burning another ~1600 quota units.
        if (ytJob.video_id) {
          setExistingYouTube({
            video_id:      ytJob.video_id,
            title:         ytJob.title || null,
            thumbnail_url: `https://i.ytimg.com/vi/${ytJob.video_id}/hqdefault.jpg`,
            privacy:       ytJob.privacy || null,
            upload_status: 'uploaded',
            watch_url:     ytJob.watch_url
              || `https://www.youtube.com/watch?v=${ytJob.video_id}`,
            published_at:  ytJob.finished_at || null,
            view_count:    null,
          });
        }
      }
      return;
    }
    if (!ytPollRef.current) {
      ytPollRef.current = window.setInterval(async () => {
        try {
          const updated = await episodesAPI.youtubeJob(ytJob.job_id);
          setYtJob(updated);
        } catch (e: any) {
          setYtError(e?.message || 'job poll failed');
        }
      }, 4000);
    }
    return () => {
      if (ytPollRef.current) {
        window.clearInterval(ytPollRef.current);
        ytPollRef.current = null;
      }
    };
  }, [ytJob]);

  const buildRequest = (): DraftRequest => ({
    site,
    // User now types just the hook ("Beautiful and Terrible as the Dawn").
    // We derive the WP-convention title ("Episode 4 - {hook}") client-side
    // and send THAT to /draft, which uses it as wp_posts.post_title verbatim.
    // For Megaphone, /megaphone-publish receives the same string and
    // runs _clean_megaphone_title() on it to produce "S14E04 - {hook}".
    // Both backend behaviors are unchanged; this just centralizes the
    // hook → WP-title transform in the UI so the user types less.
    title: computeWpTitle(title, episodeNumber),
    description: description.trim(),
    // Convert browser-local → UTC ISO so the backend (which treats
    // naive datetimes as UTC) interprets the user's wall-clock
    // intention correctly. Without this, a user in PDT typing
    // "6 PM" would have the publish-sync cron fire at 11 AM PDT.
    release_date: localDateTimeToUtcIso(releaseDate),
    season_number: seasonNumber ? Number(seasonNumber) : null,
    season_post_id: seasonPostId ? Number(seasonPostId) : null,
    episode_number: episodeNumber ? Number(episodeNumber) : null,
    content_warning: contentWarning.trim() || null,
    extra_body: extraBody.trim() || null,
    auto_publish: autoPublish,
    og_title:           ogTitle.trim() || null,
    og_description:     ogDescription.trim() || null,
    twitter_title:      twitterTitle.trim() || null,
    twitter_description: twitterDescription.trim() || null,
  });

  const validate = (): string | null => {
    if (!title.trim() || title.trim().length < 3) return 'Title must be ≥ 3 chars';
    if (!description.trim() || description.trim().length < 10)
      return 'Description must be ≥ 10 chars';
    if (seasonNumber && Number.isNaN(Number(seasonNumber)))
      return 'Season number must be a number';
    if (episodeNumber && Number.isNaN(Number(episodeNumber)))
      return 'Episode number must be a number';
    return null;
  };

  const onPreview = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setCreated(null);
    setBusy('previewing');
    try {
      const data = await episodesAPI.preview(buildRequest());
      setPreview(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Preview failed');
      setPreview(null);
    } finally {
      setBusy('idle');
    }
  };

  const onCreate = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setBusy('creating');
    try {
      const data = await episodesAPI.createDraft(buildRequest());
      setCreated(data);
      // Smoothness: Steps 2-4 mount below the fold on create. Scroll the
      // Megaphone step into view so the next action is obvious instead of the
      // form looking like it did nothing.
      setTimeout(() => {
        step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Create failed');
    } finally {
      setBusy('idle');
    }
  };

  // ── Step 2: upload audio + artwork to S3, then create Megaphone episode
  // Three modes:
  //   1. Audio + (optional artwork)  — full upload, normal flow
  //   2. Video selected               — metadata-only Megaphone draft, no
  //                                     bytes sent to Megaphone (user does
  //                                     the .mp4 + poster manually in CMS)
  //   3. Audio mode but no audio file — also creates a metadata-only draft
  const onMegaphonePublish = async () => {
    if (!created) return;
    if (mpMediaKind === 'audio' && !audioFile) {
      setMpError('Audio file is required for audio mode (or switch to video for a metadata-only draft)');
      return;
    }
    // Megaphone requires a pubdate or it shows 'undefined:undefined
    // undefined' in the CMS. Force it now rather than letting the user
    // ship a malformed draft.
    if (!releaseDate) {
      // Safety net — the button is already disabled when releaseDate is empty
      // (with an inline hint), so this is only reachable programmatically. No
      // scroll-jack: yanking the user's scroll position is more jarring than
      // the inline message + disabled button already convey.
      setMpError('Release date is required — set it in Step 1 (Release Date / Time) before creating the Megaphone episode.');
      return;
    }
    setMpError(null);
    setMpResult(null);
    setMpWarnings([]);
    setAudioPct(0);
    setArtworkPct(0);

    // Client-side non-fatal step failures, surfaced in the UI (not just console).
    const warns: string[] = [];
    try {
      // VIDEO mode WITH a chosen file → fully programmatic upload via the
      // async job (stage .mp4 to S3 → backend chunks it to Megaphone →
      // reassemble → CreateEpisode). The job's progress is polled below.
      if (mpMediaKind === 'video' && audioFile) {
        // Optional artwork → WP featured image AND Megaphone thumbnail/cover.
        let videoArtworkGetUrl: string | undefined = undefined;
        if (artworkFile) {
          try {
            setMpStatus('uploading_artwork');
            const artPresign = await uploadsAPI.presign({
              site, kind: 'artwork', filename: artworkFile.name,
              content_type: artworkFile.type || 'image/jpeg',
            });
            await uploadsAPI.uploadToS3(artPresign.put_url, artworkFile,
              artworkFile.type || 'image/jpeg', (pct) => setArtworkPct(pct));
            videoArtworkGetUrl = artPresign.get_url;   // → Megaphone thumbnail
            await episodesAPI.sideloadArtwork({
              site, wp_post_id: created.post_id, artwork_get_url: artPresign.get_url });
          } catch (e) {
            console.warn('artwork sideload (video) failed (non-fatal):', e);
            warns.push('Featured image was not set on the WordPress post (video artwork sideload failed).');
          }
        }
        // Optional DEDICATED video thumbnail/poster (16:9) — distinct from the
        // square cover above. Sets the Megaphone episode's video thumbnail.
        let videoThumbGetUrl: string | undefined = undefined;
        if (mpVideoThumbFile) {
          try {
            const tPresign = await uploadsAPI.presign({
              site, kind: 'artwork', filename: mpVideoThumbFile.name,
              content_type: mpVideoThumbFile.type || 'image/jpeg',
            });
            await uploadsAPI.uploadToS3(tPresign.put_url, mpVideoThumbFile,
              mpVideoThumbFile.type || 'image/jpeg', (pct) => setMpVideoThumbPct(pct));
            videoThumbGetUrl = tPresign.get_url;
          } catch (e) {
            console.warn('video thumbnail upload failed (non-fatal):', e);
            warns.push('The dedicated 16:9 video poster failed to upload — the square cover will be used as the poster instead.');
          }
        }
        setMpStatus('uploading_audio');   // staging the .mp4 to S3
        const videoUpload = await uploadsAPI.uploadToS3Multipart(
          site, 'video', audioFile, (pct) => setAudioPct(pct));
        setMpStatus('creating_episode');
        const job = await episodesAPI.megaphoneVideoPublish({
          site,
          wp_post_id: created.post_id,
          title: computeWpTitle(title, episodeNumber),
          summary: description.trim(),
          video_get_url: videoUpload.get_url,
          pubdate: localDateTimeToUtcIso(releaseDate),
          season_number: seasonNumber ? Number(seasonNumber) : null,
          episode_number: episodeNumber ? Number(episodeNumber) : null,
          canonical_url: created.canonical_url,
          content_warning: contentWarning.trim() || null,
          explicit: mpExplicit === 'unspecified' ? null : mpExplicit,
          episode_type: mpEpisodeType,
          artwork_get_url: videoArtworkGetUrl ?? null,
          video_thumbnail_get_url: videoThumbGetUrl ?? null,
          original_filename: audioFile.name,
        });
        setMpVideoJob(job);   // polling effect takes over from here
        if (warns.length) setMpWarnings(warns);  // client-side WP steps (job warns come via polling)
        return;
      }

      // 1. Media upload to S3 — only if mediaKind=audio AND we have a file.
      // Video mode WITHOUT a file = metadata-only draft (user uploads the
      // .mp4 via cms.megaphone.fm).
      let mediaGetUrl: string | undefined = undefined;
      if (mpMediaKind === 'audio' && audioFile) {
        setMpStatus('uploading_audio');
        const mediaPresign = await uploadsAPI.presign({
          site, kind: mpMediaKind,
          filename: audioFile.name,
          content_type: audioFile.type || 'application/octet-stream',
        });
        await uploadsAPI.uploadToS3(
          mediaPresign.put_url, audioFile,
          audioFile.type || 'application/octet-stream',
          (pct) => setAudioPct(pct),
        );
        mediaGetUrl = mediaPresign.get_url;
      }

      // 2. Artwork (optional)
      let artworkGetUrl: string | undefined = undefined;
      if (artworkFile) {
        setMpStatus('uploading_artwork');
        const artPresign = await uploadsAPI.presign({
          site, kind: 'artwork',
          filename: artworkFile.name,
          content_type: artworkFile.type || 'application/octet-stream',
        });
        await uploadsAPI.uploadToS3(
          artPresign.put_url, artworkFile,
          artworkFile.type || 'application/octet-stream',
          (pct) => setArtworkPct(pct),
        );
        artworkGetUrl = artPresign.get_url;

        // 2a. Sideload artwork to WP as featured image — INDEPENDENT of
        //     Megaphone. Ensures og:image / twitter:image are populated
        //     even if the Megaphone create below fails or is skipped.
        try {
          await episodesAPI.sideloadArtwork({
            site,
            wp_post_id: created.post_id,
            artwork_get_url: artworkGetUrl,
          });
        } catch (sideErr: any) {
          // Non-fatal — Megaphone publish will retry sideload on its end.
          console.warn('Featured-image sideload failed (non-fatal):', sideErr);
        }
      }

      // 3. Tell Megaphone to create the episode (it will fetch from S3 async).
      // mediaGetUrl is undefined for video / metadata-only flows — Megaphone
      // creates a draft shell with no media, ready for manual upload in CMS.
      setMpStatus('creating_episode');
      const result = await episodesAPI.megaphonePublish({
        site,
        wp_post_id: created.post_id,
        // Send the WP-convention title; /megaphone-publish runs it
        // through _clean_megaphone_title() to produce the "S14E04 -
        // {hook}" form. Both work via the same hook-stripping idempotency.
        title: computeWpTitle(title, episodeNumber),
        summary: description.trim(),
        audio_get_url: mediaGetUrl ?? null,
        artwork_get_url: artworkGetUrl ?? null,
        // Megaphone treats pubdate as UTC; convert from browser-local.
        pubdate: localDateTimeToUtcIso(releaseDate),
        season_number: seasonNumber ? Number(seasonNumber) : null,
        episode_number: episodeNumber ? Number(episodeNumber) : null,
        canonical_url: created.canonical_url,
        media_type: mpMediaKind,
        episode_type: mpEpisodeType,
        explicit: mpExplicit === 'unspecified' ? null : mpExplicit,
      });
      setMpResult(result);
      // Backend reports non-fatal step failures (featured image, JSON-LD, CTA)
      // in result.warnings — surface them alongside any client-side ones.
      setMpWarnings([...warns, ...((result && result.warnings) || [])]);
      setMpStatus('done');
      // Reflect the freshly-created Megaphone episode in state so that
      // the Step 2 panel re-renders with the "already exists" banner +
      // attach-audio button — preventing the user from re-clicking
      // Create and making a duplicate.
      setExistingMegaphone({
        id: result.megaphone_episode_id,
        image_url: artworkGetUrl || null,
        thumbnail_url: null,
        audio_url: mediaGetUrl || null,
        audio_uploaded: !!mediaGetUrl,
        draft: result.draft ?? true,
        status: result.megaphone_status || null,
        pubdate: localDateTimeToUtcIso(releaseDate),
        clean_title: stripEpisodePrefix(title) || null,
      });
    } catch (e: any) {
      setMpError(
        e?.response?.data?.detail || e?.message || 'Megaphone publish failed'
      );
      setMpStatus('idle');
    }
  };

  // ── Step 3b: retry a failed YT upload WITHOUT re-uploading from S3
  // The backend persisted the original staged S3 URL so the worker can
  // be re-spawned with the same inputs — saves the ~10 GB re-upload
  // when the original failure was downstream of S3 (OAuth, transient
  // YT-API 5xx, etc.).
  const onYouTubeRetry = async () => {
    if (!ytJob) return;
    setYtError(null);
    try {
      const updated = await episodesAPI.youtubeRetry(ytJob.job_id);
      setYtJob(updated);
      setYtPhase('queued');
    } catch (e: any) {
      setYtError(
        e?.response?.data?.detail || e?.message || 'YT retry failed',
      );
    }
  };

  // ── Step 2b: attach audio to an EXISTING Megaphone episode (Resume path)
  // Used when /draft-detail (or a prior fresh-create) hydrated
  // existingMegaphone. Skips the create-episode call entirely so we
  // don't end up with a duplicate Megaphone episode on the network.
  const onMegaphoneAttachAudio = async () => {
    if (!created || !existingMegaphone) return;
    if (!audioFile) {
      setMpError('Audio file is required to attach to the existing Megaphone episode');
      return;
    }
    setMpError(null);
    setMpResult(null);
    setAudioPct(0);
    try {
      setMpStatus('uploading_audio');
      const mediaPresign = await uploadsAPI.presign({
        site, kind: 'audio',
        filename: audioFile.name,
        content_type: audioFile.type || 'application/octet-stream',
      });
      await uploadsAPI.uploadToS3(
        mediaPresign.put_url, audioFile,
        audioFile.type || 'application/octet-stream',
        (pct) => setAudioPct(pct),
      );

      setMpStatus('creating_episode');  // 'creating' covers the PATCH
      const result = await episodesAPI.megaphoneAttachAudio({
        site,
        wp_post_id: created.post_id,
        audio_get_url: mediaPresign.get_url,
      });
      setMpResult(result);
      setMpStatus('done');
      setExistingMegaphone({
        ...existingMegaphone,
        audio_url: mediaPresign.get_url,
        audio_uploaded: true,
        status: 'pending',  // Megaphone transitions not_ready → pending
      });
    } catch (e: any) {
      setMpError(
        e?.response?.data?.detail || e?.message || 'Attach audio failed'
      );
      setMpStatus('idle');
    }
  };

  // ── Step 3: upload video (+ optional thumbnail) to S3, then queue YT job
  const onYouTubePublish = async () => {
    if (!created || !videoFile) return;
    setYtError(null);
    setVideoPct(0);
    setThumbnailPct(0);
    setYtJob(null);

    try {
      // Video to S3 — multipart unconditionally. Single-PUT caps at
      // 5 GB and a typical YT-grade .mp4 for a long-form podcast is
      // 6-15 GB, so single-PUT fails with 400. Multipart also gives us
      // parallel chunk uploads (faster on most connections) and the
      // bonus that a single bad part can be retried without restarting
      // from 0 — though we don't surface retry UI yet.
      setYtPhase('uploading_video');
      const videoUpload = await uploadsAPI.uploadToS3Multipart(
        site, 'video', videoFile,
        (pct) => setVideoPct(pct),
      );

      // Thumbnail to S3 (optional)
      let thumbnailGetUrl: string | undefined = undefined;
      if (thumbnailFile) {
        setYtPhase('uploading_thumbnail');
        const thumbPresign = await uploadsAPI.presign({
          site, kind: 'artwork',     // reuse artwork kind for image staging
          filename: thumbnailFile.name,
          content_type: thumbnailFile.type || 'image/jpeg',
        });
        await uploadsAPI.uploadToS3(
          thumbPresign.put_url, thumbnailFile,
          thumbnailFile.type || 'image/jpeg',
          (pct) => setThumbnailPct(pct),
        );
        thumbnailGetUrl = thumbPresign.get_url;
      }

      // Queue YT upload job
      setYtPhase('queued');
      // Per-platform overrides — fall back to the YT-convention
      // defaults that match every existing video:
      //   title:       "{show} | TTRPG Podcast | S{NN}E{NN} - {hook}"
      //   description: full WP post body (description + content
      //                warning + cast footer + CTA block + …)
      const ytTitleDefault = computeYoutubeTitle(title, site, seasonNumber, episodeNumber);
      const ytDescriptionDefault = preview?.post_content
        ? (() => {
            const tmp = document.createElement('div');
            tmp.innerHTML = preview.post_content;
            const stripped = (tmp.textContent || tmp.innerText || '').trim();
            const opener = description.trim();
            // The WP body already begins with the opener line — only prepend it
            // when the body doesn't already lead with it, or we get a duplicated
            // first line. Compare on collapsed whitespace to survive HTML→text
            // spacing differences.
            const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
            const body = norm(stripped).startsWith(norm(opener))
              ? stripped
              : `${opener}\n\n${stripped}`;
            return body.slice(0, 5000);
          })()
        : description.trim();
      const job = await episodesAPI.youtubePublish({
        site,
        wp_post_id: created.post_id,
        // If user typed clickbait, upload with it AND schedule the SEO
        // rename. If they left clickbait blank, upload directly with
        // the SEO title (no rename scheduled — backend skips when
        // initial title == seo_title).
        title:       (ytTitle.trim() || ytTitleDefault),
        description: (ytDescription.trim() || ytDescriptionDefault),
        video_get_url: videoUpload.get_url,
        thumbnail_get_url: thumbnailGetUrl ?? null,
        privacy: ytPrivacy,
        add_to_playlist: ytAddToPlaylist,
        playlist_id: ytAddToPlaylist ? (ytPlaylistId || null) : null,
        seo_title: ytTitleDefault,        // always send the SEO target
        seo_rename_days: 7,
      });
      setYtJob(job);
    } catch (e: any) {
      setYtError(e?.response?.data?.detail || e?.message || 'YouTube publish failed');
      setYtPhase('idle');
    }
  };

  // ── Patreon-ONLY standalone mode ───────────────────────────────────────
  // Post straight to Patreon with its own media + tier selection — no
  // Megaphone episode, no website post. The whole episode pipeline is skipped.
  if (patreonOnly) {
    return (
      <Stack spacing={3}>
        <Panel label="PATREON :: STANDALONE POST (no Megaphone, no website)">
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Site</InputLabel>
                <Select
                  label="Site"
                  value={site}
                  onChange={(e) => setSite(e.target.value as 'spoutlore' | 'mallbrats')}
                >
                  <MenuItem value="spoutlore">Spout Lore</MenuItem>
                  <MenuItem value="mallbrats">Mall Brats</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                Upload an audio/video file (or paste a link), pick tiers, and post
                directly to Patreon. Nothing is created on Megaphone or the website.
              </Typography>
              <Button size="small" variant="text" onClick={() => setPatreonOnly(false)}>
                ← back to full episode wizard
              </Button>
            </Stack>
          </Box>
        </Panel>
        <PatreonPublishPanel site={site} defaultTitle={title} />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <WizardSteps
        createdDone={!!created}
        megaphoneDone={!!mpResult || !!existingMegaphone}
        youtubeDone={!!existingYouTube || ytPhase === 'done'}
        publishDone={publishStatus === 'done'}
      />
      {/* Resume by post_id — escape hatch for when the tab died
          between Step 1 and Step 2 (and the dashboard's pending-
          releases list doesn't surface drafts without release_at,
          or for any draft the user wants to jump straight back to). */}
      <Panel label="RESUME :: PICK UP MID-WIZARD">
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Already created the WP draft and need to continue from Step 2 (Megaphone)
            or Step 3 (publish-now)? Paste the post_id and click Resume — the wizard
            will skip Step 1 and land you on the right step based on what's done.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Site</InputLabel>
              <Select
                label="Site"
                value={site}
                disabled={!!created}
                onChange={(e) => setSite(e.target.value as 'spoutlore' | 'mallbrats')}
              >
                <MenuItem value="spoutlore">Spout Lore</MenuItem>
                <MenuItem value="mallbrats">Mall Brats</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="WP post_id"
              placeholder="e.g. 1088"
              value={resumePostId}
              onChange={(e) => setResumePostId(e.target.value.replace(/\D/g, ''))}
              sx={{ width: 180 }}
            />
            <Button
              variant="outlined"
              disabled={busy === 'resuming' || !resumePostId}
              onClick={() => onResume(Number(resumePostId), site)}
            >
              {busy === 'resuming' ? 'RESUMING…' : '[ RESUME ]'}
            </Button>
            {created && (
              <Typography variant="caption" color="success.main" sx={{ ml: 2 }}>
                ✓ Resumed post #{created.post_id} — scroll down to Step 2
              </Typography>
            )}
          </Stack>
        </Box>
      </Panel>

      <PendingReleasesPanel onResume={onResume} />

      <Panel label="RELEASE TYPE">
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Quick-setup for special releases:
            </Typography>
            <Button
              size="small"
              variant={patreonMode ? 'contained' : 'outlined'}
              color="secondary"
              onClick={applyPatreonTeaserPreset}
            >
              🅟 Patreon Teaser (video)
            </Button>
            {patreonMode && (
              <Button size="small" variant="text" onClick={clearPatreonTeaserPreset}>
                Clear preset
              </Button>
            )}
            {/* Patreon-only: skip Megaphone + website entirely. */}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => setPatreonOnly(true)}
            >
              🅟 Patreon only (no episode)
            </Button>
          </Stack>

          {patreonMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Patreon teaser mode — pre-set for you:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0, '& li': { fontSize: 13 } }}>
                <li>Site → <strong>Spout Lore</strong> (the main feed)</li>
                <li>Season → <strong>Patreon Teasers</strong> page (no season/episode #)</li>
                <li>Megaphone → <strong>Video</strong> media + <strong>Bonus</strong> type + rating <strong>Unspecified</strong></li>
                <li>YouTube → <strong>“Add to VOD playlist” unchecked</strong></li>
              </Box>
              <Box sx={{ mt: 1.5, mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Pull from Patreon
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Search the bonus post by title and pick it. Fills the description
                  (everything before <code>[Content Warning: …]</code>) + the
                  content-warning list; anything after is dropped.
                </Typography>
                <Autocomplete<PatreonMatch>
                  size="small"
                  options={patreonOptions}
                  loading={patreonLoading}
                  filterOptions={(x) => x}                 /* server-side search */
                  getOptionLabel={(o) => o.title}
                  isOptionEqualToValue={(o, v) => o.post_id === v.post_id}
                  onInputChange={(_, val) => setPatreonInput(val)}
                  onChange={(_, val) => onSelectPatreonPost(val)}
                  noOptionsText={patreonInput ? 'No matching Patreon posts' : 'Type to search…'}
                  renderOption={(props, o) => (
                    <li {...props} key={o.post_id}>
                      <Box>
                        <Typography variant="body2">{o.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(o.published_at || '').slice(0, 10)}
                          {o.had_content_warning ? ' · has CW' : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search Patreon posts by title…"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {patreonLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {patreonPullMsg && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    {patreonPullMsg}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
                You still need to:
              </Typography>
              <Box component="ol" sx={{ pl: 2, m: 0, '& li': { fontSize: 13 } }}>
                <li>Pick the post in “Pull from Patreon” above → it fills the title, description + content warning. (Tweak any if needed.)</li>
                <li>Set the release date.</li>
                <li>Step&nbsp;2: upload the <strong>video file</strong> + artwork, then create the Megaphone episode.</li>
                <li>Step&nbsp;3 (optional): upload to YouTube if you want it there too.</li>
                <li>Step&nbsp;4: Publish everywhere.</li>
              </Box>
            </Alert>
          )}
        </Box>
      </Panel>

      <Panel label="EPISODE :: NEW DRAFT WIZARD">
        <SectionTitle>
          {`> COMPOSE A FULLY SEO-BAKED WORDPRESS DRAFT FROM MINIMAL INPUT_`}
        </SectionTitle>

        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Fills in: post_content (canonical body + content-warning + cast footer
            + internal links), Yoast title/metadesc/focuskw, JSON-LD postmeta,
            full canonical tag set, season pointer, Yoast indexable row.
            Audio/YT embed placeholders are left for the publish-sync step.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small" disabled={!!created}>
                <InputLabel>Site</InputLabel>
                <Select
                  label="Site"
                  value={site}
                  disabled={!!created}
                  onChange={(e) => setSite(e.target.value as any)}
                >
                  <MenuItem value="spoutlore">Spout Lore</MenuItem>
                  <MenuItem value="mallbrats">Mall Brats</MenuItem>
                </Select>
                {created && (
                  <FormHelperText>Locked — draft #{created.post_id} already exists on this site.</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Season</InputLabel>
                <Select
                  label="Season"
                  value={seasonPostId}
                  onChange={(e) => {
                    const id = e.target.value as string;
                    setSeasonPostId(id);
                    // Keep seasonNumber in sync: set it for numbered seasons (so
                    // Megaphone seasonNumber + JSON-LD 'Season N' still work),
                    // clear it for named pages (Patreon Teasers, Trailers, …).
                    const sel = seasons.find((s) => String(s.post_id) === id);
                    setSeasonNumber(sel?.season_number != null ? String(sel.season_number) : '');
                  }}
                >
                  <MenuItem value=""><em>— none —</em></MenuItem>
                  {seasons.map((s) => (
                    <MenuItem key={s.post_id} value={String(s.post_id)}>
                      {s.title}{!s.numbered ? '  (no number)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                label="Episode #"
                type="number"
                size="small"
                fullWidth
                value={episodeNumber}
                onChange={(e) => setEpisodeNumber(e.target.value)}
                placeholder="e.g. 4"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Episode Name (just the hook — no 'Episode N -' prefix)"
                size="small"
                fullWidth
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`e.g. "Beautiful and Terrible as the Dawn"`}
                helperText={
                  // Show what'll actually land on each surface. Built
                  // from the hook + Season/Episode fields so the user
                  // sees the formatted strings before clicking Create.
                  // Idempotent — if you typed "Episode 4 - foo" out of
                  // habit, we strip and re-prefix correctly.
                  (() => {
                    if (!title.trim()) {
                      return 'Type just the episode name. We derive the WP / Megaphone / SEO titles from this + the Season/Episode numbers.';
                    }
                    const wp     = computeWpTitle(title, episodeNumber);
                    const mp     = computeMegaphoneTitle(title, seasonNumber, episodeNumber);
                    const yoast  = computeYoastTitle(wp, site, seasonNumber);
                    return (
                      <>
                        WP post_title: <strong>{wp || '—'}</strong>
                        {' · '}
                        Megaphone: <strong>{mp || '—'}</strong>
                        {' · '}
                        SEO title (og/tw): <strong>{yoast || '—'}</strong>
                      </>
                    );
                  })() as any
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="One-line Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="The hook paragraph above the fold"
                helperText={`${description.length} chars (min 10)`}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Content Warning"
                size="small"
                fullWidth
                value={contentWarning}
                onChange={(e) => setContentWarning(e.target.value)}
                placeholder="Spiders, Cliffs, Body Horror"
                helperText="Comma-separated; rendered as [Content Warning: …]"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {(() => {
                // <input type="datetime-local"> returns a naive ISO
                // string ("2026-05-23T18:30") in the browser's local
                // timezone. Without conversion, the backend treats it
                // as UTC — so a user in Toronto typing "6 PM" would
                // have the system publish at 2 PM Eastern (4h early).
                //
                // We show: (a) the local tz being used as input, and
                // (b) the UTC equivalent we'll actually send. The
                // value passed to /draft + /megaphone-publish is the
                // UTC-converted ISO string (see buildRequest).
                const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                let utcPreview: string | null = null;
                if (releaseDate) {
                  try {
                    // Interpret the naive value as local browser time,
                    // then read its UTC ISO form.
                    const d = new Date(releaseDate);
                    if (!isNaN(d.getTime())) {
                      utcPreview = d.toISOString();
                    }
                  } catch { /* ignore */ }
                }
                return (
                  <TextField
                    label={`Release Date / Time (${localTz}) — required for Megaphone`}
                    type="datetime-local"
                    size="small"
                    fullWidth
                    required
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!releaseDate}
                    helperText={
                      releaseDate
                        ? `Will send as UTC: ${utcPreview ?? '(invalid date)'}  ·  Megaphone pubdate uses UTC`
                        : `REQUIRED — Megaphone shows 'undefined:undefined' without a pubdate. Browser tz: ${localTz}`
                    }
                  />
                );
              })()}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={PUBLISH_SYNC_CRON_ENABLED && autoPublish}
                    onChange={(e) => setAutoPublish(e.target.checked)}
                    disabled={!PUBLISH_SYNC_CRON_ENABLED || !releaseDate}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ color: PUBLISH_SYNC_CRON_ENABLED ? 'text.primary' : 'text.disabled' }}
                  >
                    <strong>Auto-publish on release date</strong> — cron flips
                    WP draft → publish + Megaphone draft → published + YT
                    private → public when the time hits.{' '}
                    {!PUBLISH_SYNC_CRON_ENABLED && (
                      <em>(currently disabled — flip enabled=true on
                      etl_schedule.publish_sync row to re-arm)</em>
                    )}
                    {PUBLISH_SYNC_CRON_ENABLED && !releaseDate && (
                      <em>(set a release date to enable)</em>
                    )}
                  </Typography>
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Extra Body (optional HTML)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={extraBody}
                onChange={(e) => setExtraBody(e.target.value)}
                placeholder="Inserted between description and footer"
              />
            </Grid>

            {/* Social-card overrides — optional. Blank = use the
                templated defaults computed below (og/tw title =
                yoast_title, og/tw description = bare description with
                NO "— A …actual play TTRPG comedy podcast" suffix).
                These match the format used by every existing post; only
                fill in to override for an unusual share-copy case.
                Defaults are computed live as you type — no need to
                click Preview first. */}
            {(() => {
              // Use the derived WP title (Episode N - hook) as input to
              // the Yoast SEO title formatter, matching what /draft will
              // actually compute server-side.
              const wpTitleForYoast  = computeWpTitle(title, episodeNumber);
              const defaultTitleStr  = computeYoastTitle(wpTitleForYoast, site, seasonNumber);
              const defaultDescStr  = computeSocialDescription(description);
              return (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                      <strong>Social card overrides (optional)</strong> — leave blank
                      to use the templated defaults shown below each field
                      (matches the format used by every existing SL/MB post:
                      title = SEO title verbatim, description = bare hook
                      with NO "&mdash; A …actual play TTRPG comedy podcast"
                      tagline that the SERP metadesc gets).
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth size="small"
                      label="Facebook (OpenGraph) title"
                      placeholder={defaultTitleStr || 'Type a title above to see the default'}
                      value={ogTitle}
                      onChange={(e) => setOgTitle(e.target.value)}
                      helperText={defaultTitleStr
                        ? `Will use: ${defaultTitleStr}`
                        : 'Will use: (your title) | (show name)'} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth size="small"
                      label="Twitter card title"
                      placeholder={defaultTitleStr || 'Type a title above to see the default'}
                      value={twitterTitle}
                      onChange={(e) => setTwitterTitle(e.target.value)}
                      helperText={defaultTitleStr
                        ? `Will use: ${defaultTitleStr}`
                        : 'Will use: (your title) | (show name)'} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth size="small" multiline minRows={2}
                      label="Facebook (OpenGraph) description"
                      placeholder={defaultDescStr || 'Type a description above to see the default'}
                      value={ogDescription}
                      onChange={(e) => setOgDescription(e.target.value)}
                      helperText={defaultDescStr
                        ? `Will use: ${defaultDescStr}`
                        : 'Will use: your bare description (no podcast-tagline suffix)'} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth size="small" multiline minRows={2}
                      label="Twitter card description"
                      placeholder={defaultDescStr || 'Type a description above to see the default'}
                      value={twitterDescription}
                      onChange={(e) => setTwitterDescription(e.target.value)}
                      helperText={defaultDescStr
                        ? `Will use: ${defaultDescStr}`
                        : 'Will use: your bare description (no podcast-tagline suffix)'} />
                  </Grid>
                </>
              );
            })()}
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              startIcon={<Visibility />}
              onClick={onPreview}
              disabled={busy !== 'idle'}
              variant="outlined"
            >
              {busy === 'previewing' ? 'PREVIEWING…' : '[ PREVIEW ]'}
            </Button>
            <Button
              startIcon={<RocketLaunch />}
              onClick={onCreate}
              disabled={busy !== 'idle'}
              variant="contained"
              color="primary"
            >
              {busy === 'creating' ? 'CREATING…' : '[ CREATE DRAFT ]'}
            </Button>
            <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
              Preview is optional — Create always builds the draft from your current inputs.
            </Typography>
          </Stack>
        </Box>
      </Panel>

      {preview && !created && (
        <Panel label="PREVIEW :: PROPOSED DRAFT">
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">SLUG</Typography>
                <Typography variant="body2" fontFamily="monospace">{preview.post_name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">POST DATE</Typography>
                <Typography variant="body2" fontFamily="monospace">{preview.post_date}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">YOAST TITLE</Typography>
                <Typography variant="body2" fontFamily="monospace">{preview.yoast_title}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary">YOAST FOCUS KW</Typography>
                <Typography variant="body2" fontFamily="monospace">{preview.yoast_focuskw}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">YOAST METADESC</Typography>
                <Typography variant="body2" fontFamily="monospace">{preview.yoast_metadesc}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  TAGS ({preview.tag_canon.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {preview.tag_canon.map(([slug, name]) => (
                    <Chip key={slug} label={name} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">POST CONTENT (rendered)</Typography>
                <Box
                  sx={{
                    mt: 1, p: 2, border: '1px solid #1e3a1e',
                    bgcolor: 'rgba(0,0,0,0.3)', maxHeight: 400, overflow: 'auto',
                    fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {preview.post_content}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Panel>
      )}

      {created && (
        <Panel label="DRAFT :: CREATED">
          <Box sx={{ p: 2 }}>
            <Alert severity="success" icon={<RocketLaunch />} sx={{ mb: 2 }}>
              Created post #{created.post_id} on {created.site} (status:{' '}
              <strong>{created.post_status}</strong>) with{' '}
              <strong>{created.tags_applied}</strong> tags applied.
            </Alert>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                startIcon={<Edit />}
                component={Link}
                href={created.edit_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
              >
                [ EDIT IN WP-ADMIN ]
              </Button>
              <Button
                startIcon={<OpenInNew />}
                component={Link}
                href={created.preview_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
              >
                [ PREVIEW ON SITE ]
              </Button>
              <Button
                startIcon={<OpenInNew />}
                component={Link}
                href={created.canonical_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
              >
                {created.canonical_url}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Audio + YouTube embed placeholders are left in post_content. Once
              Megaphone receives the audio file (Step 2 below) and the YouTube
              video is uploaded, the publish-sync job (cron) will fill them in
              and flip status to publish on the release date.
            </Typography>
          </Box>
        </Panel>
      )}

      {/* Scroll anchor for the Megaphone step (see step2Ref). */}
      <Box ref={step2Ref} sx={{ scrollMarginTop: 16 }} />
      {created && (
        <Panel label="STEP 2 :: UPLOAD TO MEGAPHONE" data-step="step-2-megaphone">
          <SectionTitle>{`> ${mpMediaKind === 'video' ? 'VIDEO' : 'AUDIO'} + ARTWORK → S3 → MEGAPHONE EPISODE_`}</SectionTitle>
          <Box sx={{ p: 2 }}>
            {existingMegaphone ? (
              <Alert
                severity={existingMegaphone.audio_uploaded ? 'success' : 'info'}
                sx={{ mb: 2 }}
              >
                <strong>Megaphone episode already exists for this post.</strong>{' '}
                Creating again would make a duplicate.{' '}
                {existingMegaphone.audio_uploaded
                  ? 'Audio is already attached — move on to Step 3 (YouTube) or Step 4 (Publish Now).'
                  : 'Choose an audio file below and use [ ATTACH AUDIO TO EXISTING EPISODE ] to PATCH it onto the existing draft.'}
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5, alignItems: 'flex-start' }}>
                  {existingMegaphone.image_url && (
                    <Box
                      component="img"
                      src={existingMegaphone.image_url}
                      alt="Episode cover"
                      sx={{
                        width: 120, height: 120, objectFit: 'cover',
                        border: '1px solid', borderColor: 'primary.main',
                        borderRadius: 0.5, flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ fontSize: '0.85em', fontFamily: 'monospace', lineHeight: 1.6 }}>
                    <div><strong>Episode ID:</strong> {existingMegaphone.id}</div>
                    {existingMegaphone.clean_title && (
                      <div><strong>Clean title:</strong> {existingMegaphone.clean_title}</div>
                    )}
                    <div>
                      <strong>Status:</strong>{' '}
                      <code>{existingMegaphone.status || '—'}</code>
                      {' · '}
                      <strong>Draft:</strong> {existingMegaphone.draft ? 'yes' : 'no'}
                    </div>
                    <div>
                      <strong>Media (audio/video):</strong>{' '}
                      {existingMegaphone.audio_uploaded ? '✓ uploaded' : '✗ not uploaded yet'}
                    </div>
                    <div>
                      <strong>Episode artwork (cover):</strong>{' '}
                      {existingMegaphone.image_url ? '✓ uploaded' : '✗ not uploaded'}
                    </div>
                    <div>
                      <strong>Video thumbnail:</strong>{' '}
                      {existingMegaphone.thumbnail_url ? '✓ uploaded' : '✗ not uploaded'}
                    </div>
                    {existingMegaphone.pubdate && (
                      <div><strong>Pubdate:</strong> {existingMegaphone.pubdate}</div>
                    )}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    href={`https://cms.megaphone.fm/organizations/${site === 'spoutlore' ? 'a8c78ae2-002f-11ec-a62f-27197db5d9e8' : '0efac72e-6e10-11ea-acc7-af432fd72393'}/podcasts/${site === 'spoutlore' ? '3cbe7146-2a64-11ef-8699-bf9cf665fd1f' : '4b21c904-2a64-11ef-8d10-aff00e4f6495'}/episodes/${existingMegaphone.id}/edit`}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<OpenInNew />}
                    sx={{ fontWeight: 700 }}
                  >
                    [ OPEN IN MEGAPHONE CMS → UPLOAD .MP4 / EDIT ]
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ mt: 1.5, display: 'block', opacity: 0.8 }}>
                  To recreate from scratch, delete the episode in{' '}
                  <Link href="https://cms.megaphone.fm/" target="_blank" rel="noreferrer">
                    cms.megaphone.fm
                  </Link>{' '}
                  first (this wizard intentionally has no delete path —
                  the podcast feed is protected at the architectural level).
                </Typography>
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Files upload directly from your browser to S3 (presigned URL,
                ~15 min lifespan). Megaphone fetches them async — the episode
                will sit as <strong>draft</strong> on Megaphone until the publish-day
                cron flips it. The MP episode_id gets saved as postmeta on
                WP post #{created.post_id}.
              </Typography>
            )}

            {mpMediaKind === 'video' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Video selected — this wizard creates a metadata-only Megaphone draft.</strong>
                {' '}Megaphone has no documented video upload API, so we'll create the
                episode shell here (title, summary, season/episode, pubdate, canonical
                URL, link to WP post #{created.post_id}) and you upload the .mp4 +
                poster manually via{' '}
                <Link href="https://cms.megaphone.fm/" target="_blank" rel="noreferrer">
                  cms.megaphone.fm
                </Link>
                . The audio file picker is disabled for this mode.
                <br />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', opacity: 0.8 }}>
                  Source:{' '}
                  <Link href="https://support.megaphone.fm/en/articles/12043267"
                        target="_blank" rel="noreferrer">
                    Megaphone — Video Distribution on Spotify
                  </Link>
                </Typography>
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Media type</InputLabel>
                  <Select
                    label="Media type"
                    value={mpMediaKind}
                    onChange={(e) => {
                      setMpMediaKind(e.target.value as 'audio' | 'video');
                      setAudioFile(null);
                      setAudioPct(0);
                    }}
                    disabled={mpStatus !== 'idle'}
                  >
                    <MenuItem value="audio">Audio (MP3 / WAV / M4A)</MenuItem>
                    <MenuItem value="video">Video (MP4 — video podcast)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Megaphone episode type + rating — must match what's
                  set on existing episodes. Regular eps = full + clean;
                  Patreon bonus eps override episode_type=bonus. */}
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Episode type</InputLabel>
                  <Select
                    label="Episode type"
                    value={mpEpisodeType}
                    onChange={(e) => setMpEpisodeType(e.target.value as any)}
                    disabled={mpStatus !== 'idle'}
                  >
                    <MenuItem value="full">Full (regular episode)</MenuItem>
                    <MenuItem value="bonus">Bonus (Patreon / behind-the-scenes)</MenuItem>
                    <MenuItem value="trailer">Trailer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Episode rating</InputLabel>
                  <Select
                    label="Episode rating"
                    value={mpExplicit}
                    onChange={(e) => setMpExplicit(e.target.value as any)}
                    disabled={mpStatus !== 'idle'}
                  >
                    <MenuItem value="clean">Clean (default)</MenuItem>
                    <MenuItem value="explicit">Explicit</MenuItem>
                    <MenuItem value="unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={mpMediaKind === 'video' ? <YouTube /> : <AudioFile />}
                  disabled={mpStatus !== 'idle'}
                >
                  {audioFile
                    ? `${audioFile.name} (${(audioFile.size / 1024 / 1024).toFixed(1)} MB)`
                    : mpMediaKind === 'video'
                      ? '[ CHOOSE VIDEO FILE ]'
                      : '[ CHOOSE AUDIO FILE ]'}
                  <input
                    type="file"
                    accept={mpMediaKind === 'video'
                      ? 'video/*,.mp4,.mov,.m4v'
                      : 'audio/*,.mp3,.wav,.m4a,.aac'}
                    hidden
                    onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {(mpStatus === 'uploading_audio' || audioPct > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={audioPct} />
                    <Typography variant="caption">{audioPct}%</Typography>
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<Image />}
                  disabled={mpStatus !== 'idle'}
                >
                  {artworkFile
                    ? `${artworkFile.name} (${(artworkFile.size / 1024).toFixed(0)} KB)`
                    : '[ CHOOSE EPISODE COVER ARTWORK (optional) ]'}
                  <input
                    type="file"
                    accept="image/*,.jpg,.jpeg,.png"
                    hidden
                    onChange={(e) => setArtworkFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {(mpStatus === 'uploading_artwork' || artworkPct > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={artworkPct} />
                    <Typography variant="caption">{artworkPct}%</Typography>
                  </Box>
                )}
              </Grid>

              {/* Video episodes have a SEPARATE 16:9 video thumbnail/poster
                  (distinct from the square cover above). */}
              {mpMediaKind === 'video' && (
                <Grid size={{ xs: 12 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    component="label"
                    startIcon={<Image />}
                    disabled={mpStatus !== 'idle'}
                  >
                    {mpVideoThumbFile
                      ? `${mpVideoThumbFile.name} (${(mpVideoThumbFile.size / 1024).toFixed(0)} KB)`
                      : '[ CHOOSE MEGAPHONE VIDEO THUMBNAIL — 16:9 poster (optional) ]'}
                    <input
                      type="file"
                      accept="image/*,.jpg,.jpeg,.png"
                      hidden
                      onChange={(e) => setMpVideoThumbFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Separate from the cover — this is the poster shown for the video on
                    Megaphone. If left empty, the cover (or an auto-picked video frame) is used.
                  </Typography>
                  {mpVideoThumbPct > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress variant="determinate" value={mpVideoThumbPct} />
                      <Typography variant="caption">{mpVideoThumbPct}%</Typography>
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>

            {mpError && (
              <Alert severity="error" sx={{ mt: 2 }}>{mpError}</Alert>
            )}

            {mpVideoJob && (
              <Box sx={{ mt: 2 }}>
                {mpVideoJob.status !== 'done' && mpVideoJob.status !== 'error' && (
                  <>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Uploading video to Megaphone — {mpVideoJob.status} ({mpVideoJob.progress_pct}%)
                    </Typography>
                    <LinearProgress variant="determinate" value={mpVideoJob.progress_pct} />
                    <Typography variant="caption" color="text.secondary">
                      Runs in the background — keep this tab open. A multi-GB video takes a few minutes.
                    </Typography>
                  </>
                )}
                {mpVideoJob.status === 'done' && (
                  <Alert severity="success">
                    Megaphone video episode created (draft).{' '}
                    {mpVideoJob.cms_edit_url && (
                      <Link href={mpVideoJob.cms_edit_url} target="_blank" rel="noopener">
                        Open in Megaphone CMS
                      </Link>
                    )}
                  </Alert>
                )}
                {/* Non-fatal step failures (artwork/poster/WP link/JSON-LD) are
                    recorded on the job even when it finishes — surface them so a
                    partial publish is never silent. */}
                {mpVideoJob.status === 'done' && mpVideoJob.error && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {mpVideoJob.error}
                  </Alert>
                )}
                {mpVideoJob.status === 'error' && (
                  <Alert
                    severity="error"
                    action={mpVideoJob.retryable ? (
                      <Button color="inherit" size="small" onClick={async () => {
                        try {
                          const u = await episodesAPI.megaphoneVideoRetry(mpVideoJob.job_id);
                          setMpVideoJob(u);
                        } catch (e: any) {
                          setMpError(e?.response?.data?.detail || e?.message || 'retry failed');
                        }
                      }}>RETRY</Button>
                    ) : undefined}
                  >
                    Megaphone video upload failed: {mpVideoJob.error}
                  </Alert>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              {existingMegaphone ? (
                // Resume / post-create path: episode already exists, so the
                // only safe action is to attach audio (if not already
                // uploaded). The destructive Create button is hidden.
                <Button
                  startIcon={<CloudUpload />}
                  onClick={onMegaphoneAttachAudio}
                  disabled={
                    mpStatus !== 'idle' ||
                    !audioFile ||
                    existingMegaphone.audio_uploaded
                  }
                  variant="outlined"
                  color="primary"
                  sx={{ minHeight: 44, fontWeight: 700 }}
                >
                  {mpStatus === 'idle' && (
                    existingMegaphone.audio_uploaded
                      ? '[ AUDIO ALREADY ATTACHED ]'
                      : '[ ATTACH AUDIO TO EXISTING EPISODE ]'
                  )}
                  {mpStatus === 'uploading_audio' && 'UPLOADING AUDIO…'}
                  {mpStatus === 'creating_episode' && 'PATCHING MEGAPHONE…'}
                  {mpStatus === 'done' && 'DONE'}
                </Button>
              ) : (
                <Button
                  startIcon={<CloudUpload />}
                  onClick={onMegaphonePublish}
                  disabled={
                    mpStatus !== 'idle' ||
                    // Audio mode requires the audio file; video mode skips
                    // the file (Megaphone has no API video upload).
                    (mpMediaKind === 'audio' && !audioFile) ||
                    // Megaphone needs a pubdate — without it the CMS shows
                    // 'undefined:undefined undefined' and the episode can't
                    // be published.
                    !releaseDate
                  }
                  // outlined variant — the CRT theme's primary-green
                  // contained button sometimes renders text the same color
                  // as the background (green-on-green), making the label
                  // invisible. Outlined keeps the label readable.
                  variant="outlined"
                  color="primary"
                  sx={{ minHeight: 44, fontWeight: 700 }}
                >
                  {mpStatus === 'idle' && (
                    mpMediaKind === 'video'
                      ? (audioFile
                          ? '[ UPLOAD VIDEO + CREATE MEGAPHONE EPISODE ]'
                          : '[ CREATE METADATA-ONLY MEGAPHONE DRAFT ]')
                      : audioFile
                        ? '[ UPLOAD + CREATE MEGAPHONE EPISODE ]'
                        : '[ CREATE METADATA-ONLY MEGAPHONE DRAFT ]'
                  )}
                  {mpStatus === 'uploading_audio' && (mpMediaKind === 'video' ? 'STAGING VIDEO…' : 'UPLOADING AUDIO…')}
                  {mpStatus === 'uploading_artwork' && 'UPLOADING ARTWORK…'}
                  {mpStatus === 'creating_episode' && 'CREATING ON MEGAPHONE…'}
                  {mpStatus === 'done' && 'DONE'}
                </Button>
              )}
              {/* Say WHY the button is disabled instead of leaving a dead control. */}
              {!existingMegaphone && mpStatus === 'idle' && (!releaseDate || (mpMediaKind === 'audio' && !audioFile)) && (
                <Typography variant="caption" sx={{ alignSelf: 'center', color: 'warning.main' }}>
                  {!releaseDate
                    ? 'Set a Release Date in Step 1 to enable this.'
                    : 'Choose an audio file above (or switch to Video for a metadata-only draft).'}
                </Typography>
              )}
            </Stack>

            {/* Non-fatal step failures (featured image, poster, JSON-LD, CTA) —
                surfaced so a partial publish is never silent. Shown for both the
                audio (mpResult) and video (mpVideoJob) flows. */}
            {mpWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Published, but {mpWarnings.length === 1 ? 'one step' : 'some steps'} didn't complete:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {mpWarnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </Alert>
            )}

            {mpResult && (
              <>
                <Alert severity="success" icon={<RocketLaunch />} sx={{ mt: 2 }}>
                  Megaphone episode <strong>{mpResult.megaphone_episode_id}</strong>{' '}
                  created in <em>draft</em> mode. Audio + artwork are processing
                  in the background. Linked to WP post #{mpResult.wp_post_id}.
                </Alert>
                {mpResult.media_type === 'video' && mpResult.cms_edit_url && (
                  // Video uploads can't go through the public Megaphone
                  // API and the internal GraphQL escape hatch can't
                  // attach uploaded files to a specific episode either.
                  // The ONLY path to get the .mp4 onto this episode is
                  // the Megaphone CMS web UI's upload widget — give the
                  // user a one-click deep-link straight there.
                  <Alert severity="warning" icon={<CloudUpload />} sx={{ mt: 2 }}>
                    <strong>Next: upload the .mp4 in the Megaphone CMS.</strong>{' '}
                    Megaphone has no API path for video files; the CMS edit
                    page below is the only place to drop the .mp4 (and audio
                    if you have it). Megaphone transcodes async + the episode
                    flips from <code>not_ready</code> → <code>pending</code> →{' '}
                    <code>complete</code>.
                    <Box sx={{ mt: 1.5 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        href={mpResult.cms_edit_url}
                        target="_blank"
                        rel="noreferrer"
                        startIcon={<OpenInNew />}
                        sx={{ fontWeight: 700 }}
                      >
                        [ OPEN MEGAPHONE EDIT PAGE → UPLOAD .MP4 ]
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{
                      display: 'block', mt: 1, opacity: 0.8, wordBreak: 'break-all',
                    }}>
                      {mpResult.cms_edit_url}
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>
        </Panel>
      )}

      {(mpResult?.megaphone_episode_id || existingMegaphone?.id) && (
        <Panel label="STEP 2b :: MIDROLL AD PLACEMENT (OPTIONAL)">
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Pre-roll and post-roll are auto-placed by Megaphone. To add{' '}
              <strong>midroll</strong> ads, open the waveform editor — play /
              scrub to the break, drop a marker (default 4&nbsp;×&nbsp;auto), and
              save. The existing pre/post-roll are preserved automatically.
              {' '}Mainly for Mall Brats audio episodes.
            </Typography>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}>
              Note: available once Megaphone has finished processing the audio
              (the ad-locator only exists after the episode is ready).
            </Typography>
            <Button variant="outlined" onClick={() => setMidrollOpen(true)}>
              ▶ Open midroll placement editor
            </Button>
          </Box>
        </Panel>
      )}

      {(mpResult?.megaphone_episode_id || existingMegaphone?.id) && (
        <MidrollPlacer
          open={midrollOpen}
          onClose={() => setMidrollOpen(false)}
          site={site}
          megaphoneEpisodeId={mpResult?.megaphone_episode_id || existingMegaphone?.id || ''}
          episodeTitle={title}
        />
      )}

      {created && (
        <Panel label="STEP 3 :: UPLOAD TO YOUTUBE (OPTIONAL)">
          <SectionTitle>{`> VIDEO FILE → S3 → YOUTUBE → AUTO-PATCH WP EMBED_`}</SectionTitle>
          <Box sx={{ p: 2 }}>
            {existingYouTube ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>YouTube video already uploaded for this post.</strong>{' '}
                Re-uploading would create a duplicate on the channel and
                burn ~1,600 quota units. Edit title/thumbnail/privacy in{' '}
                <Link href="https://studio.youtube.com/" target="_blank" rel="noreferrer">
                  YouTube Studio
                </Link>{' '}
                instead.
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5, alignItems: 'flex-start' }}>
                  {existingYouTube.thumbnail_url && (
                    <Box
                      component="a"
                      href={existingYouTube.watch_url || '#'}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        flexShrink: 0, display: 'block',
                        border: '1px solid', borderColor: 'primary.main',
                        borderRadius: 0.5, lineHeight: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={existingYouTube.thumbnail_url}
                        alt={existingYouTube.title || 'YT thumbnail'}
                        sx={{ width: 213, height: 120, objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                  <Box sx={{ fontSize: '0.85em', fontFamily: 'monospace', lineHeight: 1.6 }}>
                    <div>
                      <strong>Video ID:</strong>{' '}
                      <Link href={existingYouTube.watch_url || '#'}
                            target="_blank" rel="noreferrer">
                        {existingYouTube.video_id}
                      </Link>
                    </div>
                    {existingYouTube.title && (
                      <div><strong>Title:</strong> {existingYouTube.title}</div>
                    )}
                    <div>
                      <strong>Privacy:</strong>{' '}
                      <code>{existingYouTube.privacy || '—'}</code>
                      {existingYouTube.upload_status && (
                        <> {' · '} <strong>Status:</strong>{' '}
                          <code>{existingYouTube.upload_status}</code>
                        </>
                      )}
                    </div>
                    {existingYouTube.published_at && (
                      <div><strong>Published:</strong> {existingYouTube.published_at}</div>
                    )}
                    {typeof existingYouTube.view_count === 'number' && (
                      <div><strong>Views:</strong> {existingYouTube.view_count.toLocaleString()}</div>
                    )}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    <strong>Don't forget the end card</strong> — YT Data API can't
                    set endscreens programmatically; it's manual in Studio.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    href={`https://studio.youtube.com/video/${existingYouTube.video_id}/editor`}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<OpenInNew />}
                  >
                    [ ADD END CARD IN YT STUDIO ]
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                The video uploads to S3 first (browser → S3), then a backend
                worker downloads it, uploads to YouTube via the OAuth
                <code> autopod_writes</code> token, and adds it to the per-site
                VOD playlist (
                {site === 'spoutlore' ? "'Spout Lore Podcast | Video Episodes'" : "'Mall Brats'"}
                ). The new YT video_id is stored as postmeta on WP post
                #{created.post_id}, so the embed patcher picks it up
                automatically. <strong>This consumes ~1,600 of the channel's
                10k/day API quota</strong> — ~6 uploads max per day.
              </Typography>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<YouTube />}
                  disabled={ytPhase !== 'idle'}
                >
                  {videoFile
                    ? `${videoFile.name} (${(videoFile.size / 1024 / 1024).toFixed(1)} MB)`
                    : '[ CHOOSE VIDEO FILE (.mp4) ]'}
                  <input
                    type="file"
                    accept="video/*,.mp4,.mov,.m4v"
                    hidden
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {(ytPhase === 'uploading_video' || videoPct > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption">Video → S3: {videoPct}%</Typography>
                    <LinearProgress variant="determinate" value={videoPct} />
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  component="label"
                  startIcon={<Image />}
                  disabled={ytPhase !== 'idle'}
                >
                  {thumbnailFile
                    ? `${thumbnailFile.name} (${(thumbnailFile.size / 1024).toFixed(0)} KB)`
                    : '[ CHOOSE YT THUMBNAIL (optional, ≤2MB, 16:9) ]'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    hidden
                    onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {(ytPhase === 'uploading_thumbnail' || thumbnailPct > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption">Thumb → S3: {thumbnailPct}%</Typography>
                    <LinearProgress variant="determinate" value={thumbnailPct} />
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                {(() => {
                  // Two-phase title workflow:
                  //   Week 0 (upload):  clickbaity title typed below
                  //                     ("when ur bro has a glow up")
                  //   Week 1+ (auto):   SEO pipe-separated format
                  //                     "{show} | TTRPG Podcast | S{NN}E{NN} - {hook}"
                  // The clickbait drives initial view velocity (YT algo
                  // rewards strongly in week 0); the SEO title carries
                  // long-tail search after the algo boost fades.
                  const seoTitle = computeYoutubeTitle(title, site, seasonNumber, episodeNumber);
                  const renameDate = new Date(Date.now() + 7 * 86400 * 1000)
                    .toISOString().slice(0, 10);
                  return (
                    <>
                      <TextField
                        fullWidth size="small"
                        label="YouTube title (clickbait — week-0 hook for algorithm boost)"
                        placeholder='e.g. "when ur bro has a glow up"'
                        value={ytTitle}
                        onChange={(e) => setYtTitle(e.target.value)}
                        disabled={ytPhase !== 'idle'}
                        inputProps={{ maxLength: 100 }}
                        helperText={
                          ytTitle.trim()
                            ? `${ytTitle.length}/100 chars — will auto-rename to SEO title on ${renameDate}`
                            : (seoTitle
                                ? `Blank → upload directly with SEO title: ${seoTitle}`
                                : 'Type a clickbait hook here. Blank will use the SEO title directly (no rename scheduled).')
                        }
                      />
                      {seoTitle && (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5, ml: 1 }}>
                          <strong>SEO title (auto-rename target, week 1):</strong>{' '}
                          <code>{seoTitle}</code>
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth size="small"
                  multiline minRows={3} maxRows={10}
                  label="YouTube description (optional override)"
                  placeholder={
                    // Mined from existing VOD videos: description hook
                    // + content warning + cast footer + CTA block (the
                    // full WP post body, HTML-stripped). When blank, we
                    // build it automatically from preview.post_content.
                    preview?.post_content
                      ? 'Leave blank — defaults to the full WP body (hook + CW + cast footer + CTAs), matching every existing video.'
                      : 'Click PREVIEW above first; YT description will then default to the full WP body.'
                  }
                  value={ytDescription}
                  onChange={(e) => setYtDescription(e.target.value)}
                  disabled={ytPhase !== 'idle'}
                  inputProps={{ maxLength: 5000 }}
                  helperText={
                    ytDescription.trim()
                      ? `${ytDescription.length}/5000 chars (overriding default)`
                      : (preview?.post_content
                          ? 'Blank → will auto-fill with the full WP body on submit (matches existing pattern)'
                          : 'Run Preview to enable auto-fill from WP body')
                  }
                />
                <Button
                  size="small"
                  variant="text"
                  sx={{ mt: 0.5 }}
                  disabled={ytPhase !== 'idle' || !preview?.post_content}
                  onClick={() => {
                    // Manual fill is now mostly redundant (the
                    // auto-fill on submit does the same), but kept so
                    // the user can edit the body text before sending.
                    const html = preview?.post_content || '';
                    const tmp = document.createElement('div');
                    tmp.innerHTML = html;
                    const text = (tmp.textContent || tmp.innerText || '').trim();
                    setYtDescription(`${description.trim()}\n\n${text}`.slice(0, 5000));
                  }}
                >
                  [ FILL FROM WP BODY (so you can edit before submit) ]
                </Button>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Privacy</InputLabel>
                  <Select
                    label="Privacy"
                    value={ytPrivacy}
                    onChange={(e) => setYtPrivacy(e.target.value as any)}
                    disabled={ytPhase !== 'idle'}
                  >
                    <MenuItem value="private">Private</MenuItem>
                    <MenuItem value="unlisted">Unlisted</MenuItem>
                    <MenuItem value="public">Public</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ytAddToPlaylist}
                      onChange={(e) => setYtAddToPlaylist(e.target.checked)}
                      disabled={ytPhase !== 'idle'}
                    />
                  }
                  label="Add to a VOD playlist"
                />
              </Grid>
              {ytAddToPlaylist && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl size="small" fullWidth disabled={ytPhase !== 'idle'}>
                    <InputLabel>VOD playlist</InputLabel>
                    <Select
                      label="VOD playlist"
                      value={ytPlaylistId}
                      onChange={(e) => setYtPlaylistId(e.target.value)}
                      renderValue={(val) => {
                        const p = ytPlaylists.find((x) => x.id === val);
                        return p ? p.title : (val || (ytPlaylistsLoading ? 'loading…' : 'default'));
                      }}
                    >
                      {ytPlaylists.length === 0 && (
                        <MenuItem value="" disabled>
                          {ytPlaylistsLoading ? 'loading playlists…' : 'no playlists found'}
                        </MenuItem>
                      )}
                      {ytPlaylists.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.title}{p.item_count != null ? ` (${p.item_count})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {ytError && (
              <Alert severity="error" sx={{ mt: 2 }}>{ytError}</Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                startIcon={<YouTube />}
                onClick={onYouTubePublish}
                disabled={ytPhase !== 'idle' || !videoFile || !!existingYouTube}
                variant="contained"
                color="error"
              >
                {existingYouTube
                  ? '[ VIDEO ALREADY UPLOADED — EDIT IN YT STUDIO ]'
                  : (
                    <>
                      {ytPhase === 'idle' && '[ UPLOAD VIDEO + QUEUE YOUTUBE PUBLISH ]'}
                      {ytPhase === 'uploading_video' && 'STAGING VIDEO TO S3…'}
                      {ytPhase === 'uploading_thumbnail' && 'STAGING THUMBNAIL TO S3…'}
                      {ytPhase === 'queued' && 'QUEUED — UPLOADING TO YOUTUBE…'}
                      {ytPhase === 'done' && 'DONE'}
                    </>
                  )}
              </Button>
            </Stack>

            {ytJob && ytJob.status !== 'done' && ytJob.status !== 'error' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Job {ytJob.job_id.slice(0, 8)} — {ytJob.status} ({ytJob.progress_pct}%)
                </Typography>
                <LinearProgress variant="determinate" value={ytJob.progress_pct} />
              </Box>
            )}

            {ytJob && ytJob.status === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}
                     action={ytJob.retryable ? (
                       <Button
                         color="inherit"
                         size="small"
                         variant="outlined"
                         onClick={onYouTubeRetry}
                         startIcon={<YouTube />}
                       >
                         RETRY (no re-upload)
                       </Button>
                     ) : undefined}>
                <strong>YouTube upload failed:</strong> {ytJob.error}
                {ytJob.retryable && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Video is already on S3 — retry re-runs the YouTube
                    upload step without re-staging the file. Fix the
                    underlying cause first (e.g. re-authorize{' '}
                    <code>autopod_writes</code> at{' '}
                    <Link href="https://api.mypodcastanalytics.org/oauth/status"
                          target="_blank" rel="noreferrer">/oauth/status</Link>
                    {' '}if the error mentions <code>invalid_grant</code>).
                  </Typography>
                )}
                {ytJob.retry_count != null && ytJob.retry_count > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                    Previous retries: {ytJob.retry_count}
                  </Typography>
                )}
              </Alert>
            )}

            {ytJob && ytJob.status === 'done' && ytJob.watch_url && (
              <>
                <Alert severity="success" icon={<YouTube />} sx={{ mt: 2 }}>
                  Uploaded as <strong>{ytPrivacy}</strong>:{' '}
                  <Link href={ytJob.watch_url} target="_blank" rel="noopener noreferrer">
                    {ytJob.watch_url} <OpenInNew fontSize="inherit" />
                  </Link>
                  {' — '}video_id <code>{ytJob.video_id}</code> stored on WP post
                  #{ytJob.wp_post_id}.
                  {ytJob.playlist_id && ' Added to VOD playlist.'}
                  {ytJob.thumbnail_set && ' Custom thumbnail set ✓.'}
                </Alert>
                {/* A non-fatal step (e.g. thumbnail rejected) is recorded on the
                    job even though the video published — surface it so it's never
                    a silent failure the user only finds in YouTube Studio. */}
                {ytJob.error && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {ytJob.error}
                  </Alert>
                )}
                {/* End cards aren't programmable via the YouTube Data API —
                    Google has explicitly declined to support endscreen edits
                    on the public v3 API (see issuetracker.google.com/35167568).
                    The only options are (a) a default endscreen template in
                    Studio settings, or (b) per-video manual setup. Surface
                    the manual deep-link so the user doesn't forget. */}
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>⚠ Next: add end card / end screens manually.</strong>{' '}
                  YouTube's Data API doesn't expose endscreen editing. Open the
                  video in YouTube Studio and add an end card (subscribe button,
                  link to the latest episode, etc.).
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      href={`https://studio.youtube.com/video/${ytJob.video_id}/editor`}
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<OpenInNew />}
                      sx={{ fontWeight: 700 }}
                    >
                      [ OPEN IN YT STUDIO → ADD END CARD ]
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
                    Tip: set a default end-card template once in Studio →
                    Settings → Upload defaults → Advanced. Every future
                    upload then auto-gets it — zero manual work per video.
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        </Panel>
      )}

      {created && (
        <Panel label="STEP 4 :: PUBLISH EVERYWHERE">
          <SectionTitle>{`> ONE BUTTON :: WP DRAFT→PUBLISH + MEGAPHONE→LIVE + YT→PUBLIC_`}</SectionTitle>
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 2 }}>
              Single button to ship the episode across every platform NOW.
              Calls <code>/publish-now</code> which:
              <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
                <li>Flips the WP post <code>draft → publish</code> (sets <code>post_date</code> to now)</li>
                <li>Writes the tracked audio URL (podtrac + swap.fm prefix wrapping the Megaphone download URL) to the WP <code>episode_url</code> postmeta — the player picks it up via ACF</li>
                <li>PATCHes Megaphone <code>draft=false</code> so the RSS feed goes out</li>
                <li>Refreshes JSON-LD postmeta with the now-known <code>contentUrl</code> + <code>duration</code></li>
                <li>Flips the YouTube video <code>private → public</code> (the embed iframe was already injected into the WP post at upload time)</li>
                <li>Stamps <code>_autopod_published_at</code> so the cron won't re-process</li>
              </Box>
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>⚠ Irreversible-ish.</strong> After this fires the RSS
              feed goes out to every podcast app within minutes and the
              YouTube video becomes visible/searchable. Make sure both
              Megaphone (audio + video file attached + transcoded) and
              YouTube (uploaded + thumbnail + end card if you want one)
              are in the state you want BEFORE clicking.
            </Alert>

            {publishError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {publishError}
              </Alert>
            )}

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button
                startIcon={<RocketLaunch />}
                onClick={() => setPublishConfirmOpen(true)}
                disabled={publishStatus === 'publishing' || (publishStatus === 'done' && !publishForce)}
                variant="contained"
                color="error"
                size="large"
                sx={{ minHeight: 56, fontWeight: 700, fontSize: '1rem' }}
              >
                {publishStatus === 'idle'       && '[ PUBLISH EVERYWHERE NOW ]'}
                {publishStatus === 'publishing' && 'PUBLISHING…'}
                {publishStatus === 'done'       && (publishForce ? '[ FORCE RE-PUBLISH ]' : '✓ PUBLISHED')}
                {publishStatus === 'error'      && '[ RETRY PUBLISH ]'}
              </Button>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={publishForce}
                    onChange={(e) => setPublishForce(e.target.checked)}
                    disabled={publishStatus === 'publishing'}
                  />
                }
                label="Force (skip audio-ready gate + re-run an already-published episode to finish skipped platforms)"
              />
            </Stack>

            {publishResult && (
              <Alert severity="success" icon={<RocketLaunch />} sx={{ mt: 2 }}>
                <strong>Published.</strong>
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0, fontFamily: 'monospace', fontSize: '0.9em' }}>
                  <li><strong>WP:</strong>         <code>{publishResult.wp}</code></li>
                  <li><strong>Megaphone:</strong>  <code>{publishResult.megaphone}</code></li>
                  <li><strong>YouTube:</strong>    <code>{publishResult.youtube}</code></li>
                  <li><strong>episode_url:</strong> <code>{publishResult.episode_url ?? '—'}</code></li>
                  <li><strong>JSON-LD:</strong>    <code>{publishResult.jsonld}</code></li>
                  {publishResult.skipped && (
                    <li style={{ color: '#ff9' }}>
                      <strong>Skipped:</strong> {publishResult.reason}
                    </li>
                  )}
                </Box>
              </Alert>
            )}
          </Box>
        </Panel>
      )}

      {created && (
        <PatreonPublishPanel
          site={site}
          wpPostId={created.post_id}
          defaultTitle={title}
          youtubeWatchUrl={existingYouTube?.watch_url || undefined}
          seasonNumber={seasonNumber ? Number(seasonNumber) : undefined}
        />
      )}

      {/* Confirm dialog */}
      <Dialog open={publishConfirmOpen}
              onClose={() => setPublishConfirmOpen(false)}
              maxWidth="sm" fullWidth>
        <DialogTitle>Publish everywhere?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            This will go live on:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.5 }}>
            <li>spoutlore.com / mallbratspodcast.com (WP post)</li>
            <li>Megaphone RSS → Apple Podcasts, Spotify, Overcast, Pocket Casts, etc.</li>
            <li>YouTube (video flips to <strong>public</strong>)</li>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Once the RSS feed flips, podcast apps will start downloading
            within ~5-15 min. There's no clean rollback.
          </Typography>
          {publishForce && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Force is on.</strong> If Megaphone hasn't finished
              transcoding, JSON-LD will publish without a working audio URL.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishConfirmOpen(false)}>Cancel</Button>
          <Button onClick={onPublishEverywhere}
                  variant="contained" color="error" autoFocus
                  startIcon={<RocketLaunch />}>
            Yes — publish everywhere
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PublishEpisodeTab;
